import React, { useEffect, useMemo, useState } from 'react';
import {
    bp_sentiment, bp_sources, bp_influencers, bp_chart, bp_escalation,
    bp_physical_threats, bp_vaccine_initiatives, bp_vaccine_safety_misinfo,
    bp_scan_status, bp_mentions, bp_list_watchlist_entries, bp_watchlists_get,
    bp_get_retention_settings, load_takedown_sla_metrics,
} from '../../../services';
import { formatNumber, unwrap, timeAgo, toUtcDate } from './_shared';
import { Icon } from './_icons';
import ModuleIcon from '../../../Components/ModuleIcon';
import { useLanguageData, langName, platformLabel } from './LanguageAnalysis';
import { DonutChart, RankBars, SentimentBars, ColumnChart, Histogram, Sparkline, ShareLine, RingGauge } from './overviewCharts';
import { topicDefsFromEntries, topicDefsFromMonitored, buildKeywordTopics } from './topicKeywords';
import { MentionsReachChart, bucketStartMs } from './MentionsTab';
import CommonButton from '../../../Components/CommonButton';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonLoader from '../../../Components/CommonLoader';
import CommonTabs from '../../../Components/CommonTabs';
import NoDataPage from '../../../Components/NoDataPage';
import StatCard from '../../../Components/StatCard';
import StatusBadge from '../../../Components/StatusBadge';
import { lightTheme as T } from '../../../Constants/Colors';
import { SubHeading, Body } from '../../../Constants/Fonts';
import './brand-mentions.scss';

const DAYS_OPTIONS = [
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
];

const TOPIC_PAGE_SIZE = 100;
const TOPIC_MAX_PAGES = 5;

const SENTIMENTS = [
    { key: 'positive', label: 'Positive' },
    { key: 'neutral',  label: 'Neutral' },
    { key: 'negative', label: 'Negative' },
];
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
const SURFACES = [
    { key: 'physical',  label: 'Physical & activist threats' },
    { key: 'vaccine',   label: 'Initiative impersonation' },
    { key: 'vacSafety', label: 'Safety misinformation' },
];

const ALERT_TAB = {
    negative_sentiment_spike: { tab: 'mentions', label: 'Open Mentions' },
    conspiracy_narrative_spike: { tab: 'topics', label: 'Open Topic Analysis' },
    antivax_narrative_spike: { tab: 'topics', label: 'Open Topic Analysis' },
    influencer_amplification_spike: { tab: 'influencers', label: 'Open Influencers & Sources' },
};

const goTo = tab => window.dispatchEvent(new CustomEvent('bm-tab-switch', { detail: { tab } }));

// The takedown endpoints return a bare body (no `data` wrapper), so the
// shared unwrap() gives {} for them; same fallback as TakedownSlaWidget.
const unwrapBare = r => r?.statusCode?.response_data?.data ?? r?.statusCode ?? r?.data ?? {};

const pctOf = (n, total) => (total ? Math.round((n / total) * 100) : 0);
const plural = (n, one, many) => `${formatNumber(n)} ${n === 1 ? one : many}`;

function delta(cur, prev) {
    if (cur == null || prev == null) return null;
    if (!prev) return cur ? { text: 'new vs the previous period', dir: 1 } : null;
    const d = Math.round(((cur - prev) / prev) * 100);
    return { text: `${d > 0 ? '+' : ''}${d}% vs the previous period`, dir: Math.sign(d) };
}

function Panel({ title, note, link, className = '', children }) {
    return (
        <section className={`bm_ov_panel cmn_elevated_card ${className}`.trim()}>
            {(title || note || link) && (
                <div className="bm_ov_panel_head">
                    <div className="bm_ov_panel_heading">
                        {title && <SubHeading as="h3" variant={2} className="bm_ov_panel_title">{title}</SubHeading>}
                        {note && <Body as="span" variant={2} className="bm_ov_muted">{note}</Body>}
                    </div>
                    {link && (
                        <Body as="button" type="button" variant={2} weight="semibold"
                            className="bm_ov_link bm_ov_noprint" onClick={() => goTo(link.tab)}>
                            {link.label} →
                        </Body>
                    )}
                </div>
            )}
            {children}
        </section>
    );
}

const Empty = ({ children }) => <Body variant={2} className="bm_ov_muted bm_ov_line">{children}</Body>;

function SentimentFigures({ counts, total }) {
    return (
        <div className="bm_ov_sent">
            <ul className="bm_ov_sent_stats">
                {SENTIMENTS.map(s => (
                    <li key={s.key} className="bm_ov_sent_stat">
                        <span className="bm_ov_sent_head">
                            <span className={`bm_ov_swatch is-${s.key}`} aria-hidden="true" />
                            <Body as="span" variant={2} weight="bold" className="bm_ov_label">{s.label}</Body>
                        </span>
                        <SubHeading as="span" variant={1} className="bm_ov_sent_value">
                            {pctOf(counts[s.key] || 0, total)}%
                        </SubHeading>
                        <Body as="span" variant={2} className="bm_ov_muted">
                            {plural(counts[s.key] || 0, 'mention', 'mentions')}
                        </Body>
                    </li>
                ))}
            </ul>
            <div className="bm_ov_sent_bar" role="img"
                aria-label={SENTIMENTS.map(s => `${s.label} ${pctOf(counts[s.key] || 0, total)}%`).join(', ')}>
                {SENTIMENTS.map(s => (counts[s.key] || 0) > 0 && (
                    <span key={s.key} className={`bm_ov_sent_seg is-${s.key}`}
                        style={{ width: `${(counts[s.key] / Math.max(1, total)) * 100}%` }} />
                ))}
            </div>
        </div>
    );
}

function bucketLabel(b) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(b || '')) return b || '';
    return new Date(`${b}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function AttentionRow({ severity, title, detail, link }) {
    return (
        <li className="bm_ov_alert">
            <StatusBadge status={severity} dot={false} transform="uppercase">{severity}</StatusBadge>
            <div className="bm_ov_alert_text">
                <Body as="div" weight="semibold" className="bm_ov_alert_title">{title}</Body>
                {detail && <Body as="div" variant={2} className="bm_ov_muted">{detail}</Body>}
            </div>
            {link && (
                <Body as="button" type="button" variant={2} weight="semibold"
                    className="bm_ov_link bm_ov_noprint" onClick={() => goTo(link.tab)}>
                    {link.label} →
                </Body>
            )}
        </li>
    );
}

function Fact({ label, value }) {
    return (
        <li className="bm_ov_fact">
            <Body as="span" variant={2} className="bm_ov_fact_label">{label}</Body>
            <Body as="span" weight="semibold" className="bm_ov_fact_value">{value}</Body>
        </li>
    );
}

function clampToWindow(series, days, bucket) {
    let cutoff = Date.now() - days * 86400000;
    if (bucket === 'weeks') {
        const c = new Date(cutoff);
        cutoff = Date.UTC(c.getUTCFullYear(), c.getUTCMonth(), c.getUTCDate()) - ((c.getUTCDay() + 6) % 7) * 86400000;
    }
    return (series || []).filter(b => {
        const t = bucketStartMs(b && b.bucket);
        return Number.isFinite(t) && t >= cutoff;
    });
}

export default function OverviewTab() {
    const [days, setDays] = useState(30);
    const [retentionDays, setRetentionDays] = useState(null);
    const [chartMode, setChartMode] = useState('mentions_reach');
    const [loading, setLoading] = useState(true);
    const [d, setD] = useState({});
    const [topics, setTopics] = useState({ loading: true, items: [], hasDefs: true, sample: 0, total: 0, mentions: [] });

    useEffect(() => {
        let alive = true;
        bp_get_retention_settings()
            .then(res => { if (alive) setRetentionDays(unwrap(res)?.retention_days ?? null); })
            .catch(() => { });
        return () => { alive = false; };
    }, []);

    const dayOptions = useMemo(() => {
        if (!retentionDays) return DAYS_OPTIONS;
        const within = DAYS_OPTIONS.filter(o => o.value <= retentionDays);
        return within.length ? within : [DAYS_OPTIONS[0]];
    }, [retentionDays]);
    useEffect(() => {
        if (retentionDays && days > retentionDays) {
            const maxWithin = dayOptions[dayOptions.length - 1];
            if (maxWithin && maxWithin.value !== days) setDays(maxWithin.value);
        }
    }, [retentionDays, days, dayOptions]);

    const bucket = days > 30 ? 'weeks' : 'days';

    useEffect(() => {
        let alive = true;
        setLoading(true);
        Promise.allSettled([
            bp_sentiment({ days }),
            bp_sources({ days }),
            bp_influencers({ days, limit: 50 }),
            bp_chart({ days, bucket }),
            bp_escalation({ window_days: days }),
            bp_physical_threats({ days }),
            bp_vaccine_initiatives({ days }),
            bp_vaccine_safety_misinfo({ days }),
            bp_scan_status(),
            load_takedown_sla_metrics({ days }),
        ]).then(([sent, src, inf, chart, esc, phys, vac, vacS, scan, td]) => {
            if (!alive) return;
            const ok = r => (r?.status === 'fulfilled' ? r.value : null);
            setD({
                sentiment: unwrap(ok(sent)) || {},
                sources: (unwrap(ok(src)) || {}).sources || [],
                influencers: (unwrap(ok(inf)) || {}).influencers || [],
                chart: unwrap(ok(chart)) || {},
                escalation: ok(esc) ? unwrap(ok(esc)) : null,
                threats: {
                    physical: unwrap(ok(phys)) || {},
                    vaccine: unwrap(ok(vac)) || {},
                    vacSafety: unwrap(ok(vacS)) || {},
                },
                scan: unwrap(ok(scan)) || {},
                takedowns: ok(td) ? unwrapBare(ok(td)) : {},
            });
            setLoading(false);
        });
        return () => { alive = false; };
    }, [days, bucket]);

    useEffect(() => {
        let alive = true;
        setTopics(t => ({ ...t, loading: true }));
        const loadSample = async () => {
            const rows = [];
            let total = 0;
            for (let page = 1; page <= TOPIC_MAX_PAGES; page += 1) {
                const body = unwrap(await bp_mentions({ days, page, page_size: TOPIC_PAGE_SIZE, sort: '-posted_at' })) || {};
                const got = body.results || [];
                total = body.total || total;
                rows.push(...got);
                if (!got.length || page >= (body.pages || 1)) break;
            }
            return { rows, total };
        };
        (async () => {
            try {
                const [entriesRes, wlRes, sample] = await Promise.all([
                    bp_list_watchlist_entries({}).catch(() => null),
                    bp_watchlists_get().catch(() => null),
                    loadSample(),
                ]);
                const defs = [
                    ...topicDefsFromEntries((unwrap(entriesRes) || {}).entries || []),
                    ...topicDefsFromMonitored((unwrap(wlRes) || {}).monitored_topics || []),
                ];
                const items = defs.length
                    ? buildKeywordTopics(sample.rows, [], days, defs)
                        .filter(t => t && t.mentions > 0)
                        .sort((a, b) => b.mentions - a.mentions)
                        .slice(0, 5)
                    : [];
                if (alive) {
                    setTopics({
                        loading: false, items, hasDefs: defs.length > 0,
                        sample: sample.rows.length, total: sample.total, mentions: sample.rows,
                    });
                }
            } catch (_) {
                if (alive) setTopics({ loading: false, items: [], hasDefs: true, sample: 0, total: 0, mentions: [] });
            }
        })();
        return () => { alive = false; };
    }, [days]);

    const hours = useMemo(() => {
        const counts = Array(24).fill(0);
        let used = 0;
        (topics.mentions || []).forEach(m => {
            // posted_at equals scanned_at when the source gave no timestamp: skip those.
            if (!m.posted_at || m.posted_at === m.scanned_at) return;
            const dt = toUtcDate(m.posted_at);
            if (!dt) return;
            counts[dt.getHours()] += 1;
            used += 1;
        });
        const hh = h => String(h).padStart(2, '0');
        const bins = counts.map((v, h) => ({ key: h, label: hh(h), value: v, tip: `${hh(h)}:00 to ${hh((h + 1) % 24)}:00` }));
        const peak = bins.reduce((best, b) => (b.value > best.value ? b : best), bins[0]);
        return { used, bins, peak };
    }, [topics.mentions]);

    // bp_demographics via the shared hook; the params object must be stable.
    const langParams = useMemo(() => ({ days }), [days]);
    const { data: langData, loading: langLoading } = useLanguageData(langParams);

    const windowLabel = (dayOptions.find(o => o.value === days) || {}).label || `Last ${days} days`;
    const windowText = windowLabel.toLowerCase();
    const today = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

    const total = d.sentiment?.total || 0;
    const bySent = d.sentiment?.by_sentiment || {};
    const counts = { positive: bySent.positive || 0, neutral: bySent.neutral || 0, negative: bySent.negative || 0 };
    const netSentiment = pctOf(counts.positive, total) - pctOf(counts.negative, total);
    const sources = d.sources || [];
    const sourceTotal = sources.reduce((a, s) => a + (s.mentions || 0), 0);
    const voices = [...(d.influencers || [])].sort((a, b) => (b.mentions || 0) - (a.mentions || 0));
    const riskVoices = voices.filter(v => v.risk_tagged).length;

    const otherSources = sources.slice(5);
    const otherCount = otherSources.reduce((a, s) => a + (s.mentions || 0), 0);
    const sourceSlices = [
        ...sources.slice(0, 5).map((s, i) => ({
            key: s.platform, label: platformLabel(s.platform), slot: i + 1,
            value: s.mentions || 0, pct: pctOf(s.mentions || 0, sourceTotal),
        })),
        ...(otherCount > 0 ? [{
            key: '__other', label: `Other (${otherSources.length})`, slot: 'other',
            value: otherCount, pct: pctOf(otherCount, sourceTotal),
        }] : []),
    ].filter(s => s.value > 0);

    const voiceRows = voices.slice(0, 4).filter(v => (v.mentions || 0) > 0).map(v => {
        const pos = v.sentiment?.positive || 0;
        const neg = v.sentiment?.negative || 0;
        return {
            key: (v.handle || '') + v.platform,
            label: `@${v.handle}`,
            sub: platformLabel(v.platform),
            positive: pos,
            negative: neg,
            neutral: v.sentiment?.neutral != null ? v.sentiment.neutral : Math.max(0, v.mentions - pos - neg),
            badge: v.risk_tagged
                ? <StatusBadge variant="danger" dot={false} transform="uppercase">Risk</StatusBadge>
                : null,
        };
    });

    const langs = langData.languages || [];
    const langTotal = langData.total || langs.reduce((a, l) => a + (l.count || 0), 0);
    const langRows = langs.slice(0, 6).filter(l => (l.count || 0) > 0).map(l => ({
        key: l.lang, label: langName(l.lang), value: l.count || 0, pct: pctOf(l.count || 0, langTotal),
    }));

    const negativeShare = clampToWindow(d.chart?.sentiment_series, days, bucket).map(b => {
        const sum = (b.positive || 0) + (b.neutral || 0) + (b.negative || 0);
        return {
            key: b.bucket, label: bucketLabel(b.bucket),
            value: sum ? ((b.negative || 0) / sum) * 100 : 0,
            tip: bucket === 'weeks' ? `Week ${b.bucket}` : bucketLabel(b.bucket),
        };
    });
    const esc = d.escalation;
    const mentionsDelta = esc ? delta(esc.current?.total, esc.prior?.total) : null;
    const negativeDelta = esc ? delta(esc.current?.negative, esc.prior?.negative) : null;

    const threatFindings = SURFACES.map(s => ({ ...s, findings: d.threats?.[s.key]?.detailed_findings || [] }));
    const allFindings = threatFindings.flatMap(s => s.findings);
    const sev = { Critical: 0, High: 0, Medium: 0, Low: 0 };
    allFindings.forEach(f => { if (sev[f.severity || 'Low'] != null) sev[f.severity || 'Low'] += 1; });
    const urgent = sev.Critical + sev.High;
    const needsReview = d.threats?.physical?.review_summary?.needs_review || 0;

    const td = d.takedowns || {};
    const tdTotal = Number(td.total_takedowns) || 0;
    const tdBreached = (Number(td.breached_initiate_count) || 0) + (Number(td.breached_resolve_count) || 0);
    const tdPending = (Number(td.pending_initiate_count) || 0) + (Number(td.pending_resolve_count) || 0);

    const series = clampToWindow(
        chartMode === 'sentiment' ? d.chart?.sentiment_series : d.chart?.mentions_reach_series, days, bucket);

    const scan = d.scan || {};
    const attention = [
        ...((esc?.alerts) || []).map((a, i) => ({
            key: 'esc' + i, severity: a.severity || 'Medium', title: a.title, detail: a.description,
            link: ALERT_TAB[a.kind],
        })),
        urgent > 0 && {
            key: 'threats', severity: sev.Critical > 0 ? 'Critical' : 'High',
            title: `${plural(urgent, 'high or critical threat finding', 'high or critical threat findings')}`,
            detail: needsReview > 0 ? `${plural(needsReview, 'finding is', 'findings are')} awaiting analyst review.` : null,
            link: { tab: 'threat_monitor', label: 'Open Threat Monitor' },
        },
        tdBreached > 0 && {
            key: 'takedowns', severity: 'Medium',
            title: `${plural(tdBreached, 'takedown', 'takedowns')} missed ${tdBreached === 1 ? 'its' : 'their'} SLA`,
            detail: `${formatNumber(Number(td.breached_initiate_count) || 0)} not initiated within 24h, ${formatNumber(Number(td.breached_resolve_count) || 0)} not resolved within 5 days.`,
            link: { tab: 'settings', label: 'Open Settings' },
        },
    ].filter(Boolean);

    return (
        <div className="bm_ov">
            <div className="bm_ov_print_head">
                <SubHeading as="h2" variant={1}>Brand Risk overview</SubHeading>
                <Body variant={2} className="bm_ov_muted">{windowLabel} · Generated {today}</Body>
            </div>

            <div className="bm_ov_toolbar">
                <div className="bm_ov_toolbar_status">
                    {scan.last_scan_at && (
                        <StatusBadge variant={scan.manual_only ? 'neutral' : 'success'} transform="none"
                            title={scan.cadence_minutes && !scan.manual_only
                                ? `Scans run every ${scan.cadence_minutes} minutes`
                                : 'Scans run when started manually'}>
                            {scan.manual_only ? 'Manual scans' : 'Monitoring'} · last scan {timeAgo(scan.last_scan_at)}
                        </StatusBadge>
                    )}
                </div>
                <div className="bm_ov_toolbar_controls bm_ov_noprint">
                    <CommonDropdown ariaLabel="Date range" value={days} onChange={setDays} options={dayOptions} />
                    <CommonButton text="Print / Save as PDF" variant="primary" className="bm_ov_print_btn" onClick={() => window.print()} />
                </div>
            </div>

            {loading ? (
                <Panel>
                    <div className="bm_ov_loading"><CommonLoader label="Loading your Brand Risk overview…" /></div>
                </Panel>
            ) : (
                <>
                    <div className="bm_ov_kpis">
                        <StatCard className="cmn_elevated_card"
                            label="Mentions" value={formatNumber(total)}
                            hint={mentionsDelta ? mentionsDelta.text : `${windowText} · ${plural(sources.length, 'platform', 'platforms')}`}
                            title={mentionsDelta ? 'Change in posts about your brand compared with the previous window of the same length' : undefined}
                            icon={<span className="bm_kpi_icon"><ModuleIcon name="easm" size={18} color="currentColor" /></span>} iconLeft />
                        <StatCard className="cmn_elevated_card"
                            label="Positive" value={`${pctOf(counts.positive, total)}%`}
                            hint={`${plural(counts.positive, 'mention', 'mentions')}`}
                            icon={<span className="bm_kpi_icon is-success"><ModuleIcon name="check_circle" size={18} color="currentColor" /></span>} iconLeft />
                        <StatCard className="cmn_elevated_card"
                            label="Negative" value={`${pctOf(counts.negative, total)}%`}
                            hint={negativeDelta
                                ? `${plural(counts.negative, 'mention', 'mentions')} · ${negativeDelta.text}`
                                : plural(counts.negative, 'mention', 'mentions')}
                            hintColor={negativeDelta && negativeDelta.dir > 0 ? T.danger : undefined}
                            icon={<span className={`bm_kpi_icon is-${counts.negative > 0 ? 'danger' : 'neutral'}`}><ModuleIcon name="risk" size={18} color="currentColor" /></span>} iconLeft />
                        <StatCard className="cmn_elevated_card"
                            label="Threat findings" value={formatNumber(allFindings.length)}
                            valueColor={urgent > 0 ? T.danger : undefined}
                            hint={allFindings.length ? `${sev.Critical} critical · ${sev.High} high` : 'none found'}
                            icon={<span className={`bm_kpi_icon is-${urgent > 0 ? 'danger' : 'primary'}`}><ModuleIcon name="compliance" size={18} color="currentColor" /></span>} iconLeft />
                        <StatCard className="cmn_elevated_card"
                            label="Risk-tagged voices" value={formatNumber(riskVoices)}
                            valueColor={riskVoices > 0 ? T.danger : undefined}
                            hint={riskVoices > 0 ? 'on your risk-tagged source list' : 'none in this window'}
                            icon={<span className={`bm_kpi_icon is-${riskVoices > 0 ? 'danger' : 'neutral'}`}><ModuleIcon name="violation" size={18} color="currentColor" /></span>} iconLeft />
                    </div>

                    <Panel title="Needs attention"
                        note={attention.length ? plural(attention.length, 'item', 'items') : windowLabel}>
                        {attention.length ? (
                            <ul className="bm_ov_alerts">
                                {attention.map(({ key, ...a }) => <AttentionRow key={key} {...a} />)}
                            </ul>
                        ) : (
                            <div className="bm_ov_clear">
                                <StatusBadge variant="success" dot={false} transform="none"
                                    icon={<Icon name="check" size={11} />}>
                                    All clear
                                </StatusBadge>
                                <Body variant={2} className="bm_ov_muted bm_ov_line">
                                    No sentiment escalations, urgent threats or missed takedown SLAs in the {windowText}.
                                </Body>
                            </div>
                        )}
                    </Panel>

                    {total === 0 ? (
                        <Panel>
                            <NoDataPage
                                title={`No mentions in the ${windowText}`}
                                hint="Once the scanner captures posts about your brand, the trend, sentiment, sources, voices, languages and topics appear here. Widen the date range or check your mention anchors in Settings."
                                action={<CommonButton text="Open Settings" variant="primary" onClick={() => goTo('settings')} />}
                            />
                        </Panel>
                    ) : (
                        <>
                            <div className="bm_ov_row">
                                <section className="bm_chart_card cmn_elevated_card bm_ov_trend bm_ov_wide">
                                    <div className="bm_ov_panel_head">
                                        <div className="bm_ov_panel_heading">
                                            <SubHeading as="h3" variant={2} className="bm_ov_panel_title">Mentions over time</SubHeading>
                                            <Body as="span" variant={2} className="bm_ov_muted">
                                                {windowLabel}, by {bucket === 'weeks' ? 'week' : 'day'}
                                            </Body>
                                        </div>
                                        <div className="bm_ov_noprint">
                                            <CommonTabs
                                                tabs={['Mentions', 'Sentiment']}
                                                values={['mentions_reach', 'sentiment']}
                                                active={chartMode}
                                                handlers={[() => setChartMode('mentions_reach'), () => setChartMode('sentiment')]}
                                            />
                                        </div>
                                    </div>
                                    <MentionsReachChart series={series} mode={chartMode} bucket={bucket} showSpikes={false} />
                                    <div className="bm_chart_legend">
                                        {(chartMode === 'sentiment'
                                            ? SENTIMENTS.map(s => ({ ...s, bar: true }))
                                            : [{ key: 'mentions', label: 'Mentions' }]
                                        ).map(s => (
                                            <span key={s.key} className="bm_chart_legend_item">
                                                <span className={`bm_chart_legend_dot is-${s.key}${s.bar ? ' is-bar' : ''}`} aria-hidden="true" />
                                                <Body as="span" variant={2} className="bm_chart_legend_label">{s.label}</Body>
                                            </span>
                                        ))}
                                    </div>
                                </section>

                                <Panel title="Sentiment"
                                    note={`${plural(total, 'mention', 'mentions')} · net ${netSentiment > 0 ? '+' : ''}${netSentiment} pts`}
                                    link={{ tab: 'mentions', label: 'Open Mentions' }} className="bm_chart_card">
                                    <SentimentFigures counts={counts} total={total} />
                                    <div className="bm_ov_sent_trend">
                                        <Body as="div" variant={2} weight="bold" className="bm_ov_label">
                                            Negative share by {bucket === 'weeks' ? 'week' : 'day'}
                                        </Body>
                                        {negativeShare.length > 1
                                            ? <ShareLine points={negativeShare} ariaLabel="Negative share over time" />
                                            : <Empty>Not enough days in this window to show a trend.</Empty>}
                                    </div>
                                </Panel>
                            </div>

                            <div className="bm_ov_row">
                                <Panel title="Source mix" note="share of mentions by platform"
                                    link={{ tab: 'influencers', label: 'All sources' }}>
                                    {sourceSlices.length
                                        ? <DonutChart slices={sourceSlices} total={sources.length}
                                            caption={sources.length === 1 ? 'platform' : 'platforms'} />
                                        : <Empty>No sources in this window.</Empty>}
                                </Panel>

                                <Panel title="Top voices" note="mentions by sentiment"
                                    link={{ tab: 'influencers', label: 'All voices' }}>
                                    {voiceRows.length ? (
                                        <>
                                            <SentimentBars rows={voiceRows} />
                                            <div className="bm_ov_keys">
                                                {SENTIMENTS.map(s => (
                                                    <span key={s.key} className="bm_ov_key">
                                                        <span className={`bm_ov_swatch is-${s.key}`} aria-hidden="true" />
                                                        <Body as="span" variant={2}>{s.label}</Body>
                                                    </span>
                                                ))}
                                            </div>
                                        </>
                                    ) : <Empty>No voices in this window.</Empty>}
                                </Panel>

                                <Panel title="Languages" note="share of mentions"
                                    link={{ tab: 'mentions', label: 'Open Mentions' }}>
                                    {langLoading ? (
                                        <div className="bm_ov_loading is-small"><CommonLoader label="Loading languages…" /></div>
                                    ) : langRows.length ? (
                                        <RankBars rows={langRows} />
                                    ) : <Empty>No language data in this window.</Empty>}
                                </Panel>
                            </div>
                        </>
                    )}

                    <div className="bm_ov_row">
                        <Panel title="When people post"
                            note={`mentions by hour, your local time${hours.used ? ` · ${formatNumber(hours.used)} dated posts` : ''}`}
                            className="bm_chart_card bm_ov_wide">
                            {topics.loading ? (
                                <div className="bm_ov_loading is-small"><CommonLoader label="Loading posts…" /></div>
                            ) : hours.used ? (
                                <>
                                    <Histogram bins={hours.bins} height={290} ariaLabel="Mentions by hour of day" />
                                    <Body variant={2} className="bm_ov_muted bm_ov_line bm_ov_chart_note">
                                        Busiest hour: {hours.peak.tip} ({plural(hours.peak.value, 'mention', 'mentions')}).
                                        Posts without a time at their source are left out.
                                    </Body>
                                </>
                            ) : <Empty>No dated posts in this window.</Empty>}
                        </Panel>

                        <Panel title="Top topics"
                            note={topics.sample && topics.total > topics.sample
                                ? `your scan keywords · latest ${formatNumber(topics.sample)} mentions`
                                : 'your scan keywords, daily mentions'}
                            link={{ tab: 'topics', label: 'Open Topic Analysis' }}>
                            {topics.loading ? (
                                <div className="bm_ov_loading is-small"><CommonLoader label="Matching topics…" /></div>
                            ) : !topics.hasDefs ? (
                                <Empty>Add scan keywords in Settings → Mention anchors to see topics here.</Empty>
                            ) : topics.items.length ? (
                                <ul className="bm_ov_topics">
                                    {topics.items.map(t => (
                                        <li key={t.title} className="bm_ov_topic">
                                            <div className="bm_ov_topic_text">
                                                <Body as="span" variant={2} weight="semibold" className="bm_ov_topic_name">{t.title}</Body>
                                                <Body as="span" variant={2} className="bm_ov_muted">
                                                    {plural(t.mentions, 'mention', 'mentions')} · {Math.round(t.share_of_voice_pct || 0)}% share
                                                </Body>
                                            </div>
                                            <Sparkline points={t.sparkline || []} />
                                        </li>
                                    ))}
                                </ul>
                            ) : <Empty>None of your scan keywords matched a mention in this window.</Empty>}
                        </Panel>
                    </div>

                    <div className="bm_ov_row">
                        <Panel title="Threat Monitor" note={`findings by severity · ${windowText}`}
                            link={{ tab: 'threat_monitor', label: 'Open Threat Monitor' }}>
                            <div className={`bm_ov_threats${allFindings.length ? '' : ' is-clear'}`}>
                                {allFindings.length > 0 && (
                                    <div>
                                        <ColumnChart
                                            height={170}
                                            ariaLabel={SEVERITIES.map(s => `${s} ${sev[s]}`).join(', ')}
                                            bars={SEVERITIES.map(s => ({
                                                key: s, label: s, value: sev[s], display: formatNumber(sev[s]), tone: s.toLowerCase(),
                                            }))}
                                        />
                                    </div>
                                )}
                                <ul className="bm_ov_surfaces">
                                    {threatFindings.map(s => (
                                        <li key={s.key} className="bm_ov_surface">
                                            <Body as="span" variant={2} className="bm_ov_surface_label">{s.label}</Body>
                                            {s.findings.length > 0 ? (
                                                <StatusBadge variant="warning" dot={false} transform="none">
                                                    {plural(s.findings.length, 'finding', 'findings')}
                                                </StatusBadge>
                                            ) : (
                                                <StatusBadge variant="success" dot={false} transform="none"
                                                    icon={<Icon name="check" size={11} />}>
                                                    Clear
                                                </StatusBadge>
                                            )}
                                        </li>
                                    ))}
                                    {needsReview > 0 && (
                                        <li className="bm_ov_surface">
                                            <Body as="span" variant={2} className="bm_ov_muted">
                                                {plural(needsReview, 'finding', 'findings')} awaiting analyst review
                                            </Body>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </Panel>

                        <Panel title="Takedowns" note={`SLA performance · ${windowText}`}
                            link={{ tab: 'settings', label: 'Open Settings' }} className="bm_ov_wide">
                            {tdTotal === 0 ? (
                                <Empty>No takedown requests in this window.</Empty>
                            ) : (
                                <div className="bm_ov_takedowns">
                                    <div className="bm_ov_rings">
                                        <RingGauge pct={td.sla_initiate_pct} label="Filed on time" size={96} />
                                        <RingGauge pct={td.sla_resolve_pct} label="Resolved on time" size={96} />
                                        <RingGauge
                                            pct={tdTotal > 0 ? Math.round((Number(td.total_resolved) || 0) / tdTotal * 100) : 0}
                                            label="Resolution rate" size={96} />
                                    </div>
                                    <ul className="bm_ov_facts">
                                        <Fact label="Requested" value={formatNumber(tdTotal)} />
                                        <Fact label="Resolved" value={formatNumber(Number(td.total_resolved) || 0)} />
                                        <Fact label="Pending" value={formatNumber(tdPending)} />
                                    </ul>
                                </div>
                            )}
                        </Panel>
                    </div>

                    <Body variant={2} className="bm_ov_muted bm_ov_footer">
                        FORESEE AI · Brand Protection · {windowLabel} · Generated {today}
                    </Body>
                </>
            )}
        </div>
    );
}
